// «Настройки»: режим безопасности, пауза, лимиты, раннеры, токены и подключение Claude Code, пароль, статистика.
import { useEffect, useId, useMemo, useState } from 'react'
import { api, errorMessage } from '../api.ts'
import { AgentSelect } from '../components/AgentSelect.tsx'
import { Icon } from '../components/Icon.tsx'
import { ModelInput } from '../components/ModelInput.tsx'
import { FleetControls } from '../components/FleetControls.tsx'
import { Badge, Button, Empty, ErrorBox, Field, Loading, PageHeader, Section, Stat, Toggle, cx } from '../components/ui.tsx'
import { copyText, useAsync, useBusy } from '../lib/hooks.ts'
import { SAFETY_EXPLANATIONS, SAFETY_TONES, STATUS_TONES, formatDateTime, formatUsd, timeAgo } from '../lib/format.ts'
import { refreshSettings, refreshStats, setSettings, useLive, useNow } from '../store.ts'
import { TaskLink } from '../router.tsx'
import { toast, toastError } from '../toast.ts'
import {
  SAFETY_MODE_LABELS,
  TASK_STATUSES,
  TASK_STATUS_LABELS,
  agentTime,
  type ApiToken,
  type ConnectInfo,
  type SafetyMode,
  type Settings,
  type WorkChat,
  type WorkChatMode,
} from '../types.ts'

const MODES: SafetyMode[] = ['read_only', 'pr_only', 'full']

async function patchSettings(body: Partial<Settings>): Promise<Settings | null> {
  try {
    const r = await api.patch<{ settings: Settings }>('/api/settings', body)
    if (r?.settings) setSettings(r.settings)
    return r?.settings ?? null
  } catch (e) {
    toastError(e, 'Настройки не сохранены')
    return null
  }
}

const AGENT_ZONES = [
  { id: 'Europe/Moscow', label: 'Москва (МСК)' },
  { id: 'UTC', label: 'UTC' },
  { id: 'Europe/Berlin', label: 'Берлин, Амстердам' },
  { id: 'Asia/Dubai', label: 'Дубай' },
]

export function SettingsPage() {
  const settings = useLive((s) => s.settings)
  useEffect(() => {
    void refreshSettings()
    void refreshStats()
  }, [])

  return (
    <div className="page max-w-[1160px]">
      <PageHeader title="Настройки" lede="Режим безопасности, лимиты, раннеры, токены доступа и твой пароль." />
      {!settings ? (
        <Loading />
      ) : (
        <div className="flex flex-col gap-5">
          <SafetySection settings={settings} />
          <PauseSection settings={settings} />
          <ProvidersSection key={`${settings.preferred_provider}|${settings.provider_fallback}|${JSON.stringify(settings.model_profiles)}`} settings={settings} />
          <ParamsSection
            key={`${settings.max_concurrency}|${settings.daily_budget_usd}|${settings.default_model}|${settings.blocked_hosts.join(',')}|${settings.readonly_hosts.join(',')}`}
            settings={settings}
          />
          <WorkChatsSection />
          <RunnersSection />
          <TokensSection />
          <SecretsSection />
          <PasswordSection />
          <StatsSection />
        </div>
      )}
    </div>
  )
}

function SafetySection({ settings }: { settings: Settings }) {
  const [busy, run] = useBusy()
  const choose = (m: SafetyMode) =>
    run(async () => {
      if (m === settings.safety_mode) return
      if (m === 'full' && !window.confirm('Полный режим: агенты смогут мержить и действовать на проде (каждое действие на проде — с твоим одобрением). Включить?')) return
      const s = await patchSettings({ safety_mode: m })
      if (s) toast(`Режим безопасности: ${SAFETY_MODE_LABELS[m]}`, 'success')
    })
  return (
    <Section title="Режим безопасности агентов">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3" role="radiogroup" aria-label="Режим безопасности">
        {MODES.map((m) => {
          const active = settings.safety_mode === m
          const tone = SAFETY_TONES[m]
          return (
            <button
              key={m}
              type="button"
              role="radio"
              aria-checked={active}
              disabled={busy}
              onClick={() => void choose(m)}
              className={cx(
                'flex flex-col items-start gap-2 rounded-card border p-4 text-left transition-colors',
                active ? 'border-accent/40 bg-accent-soft' : 'border-line bg-surface hover:border-line-strong hover:bg-surface-2',
              )}
            >
              <span className="flex w-full items-center gap-2">
                <span
                  aria-hidden="true"
                  className={cx('flex h-4 w-4 shrink-0 items-center justify-center rounded-full border', active ? 'border-accent' : 'border-line-strong')}
                >
                  {active ? <span className="h-2 w-2 rounded-full bg-accent" /> : null}
                </span>
                <span className="text-[14px] font-semibold text-fg">{SAFETY_MODE_LABELS[m]}</span>
                {tone !== 'neutral' ? <span className={cx('stbadge ml-auto', tone)}>{m === 'full' ? 'прод' : 'ветки'}</span> : <span className="stbadge ok ml-auto">безопасно</span>}
              </span>
              <span className="text-[13px] leading-snug text-muted">{SAFETY_EXPLANATIONS[m]}</span>
            </button>
          )
        })}
      </div>
      <p className="mt-3 text-[12.5px] text-muted">
        Режим можно переопределить для отдельной задачи в её карточке. Запрещённые хосты агенты не трогают ни в каком режиме.
      </p>
    </Section>
  )
}

function PauseSection({ settings }: { settings: Settings }) {
  const [busy, run] = useBusy()
  return (
    <Section>
      <div className="flex flex-wrap items-center gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-[15px] font-semibold">
            Глобальная пауза
            {settings.paused ? (
              <Badge tone="warn" dot>
                включена
              </Badge>
            ) : (
              <Badge tone="ok" dot>
                агенты работают
              </Badge>
            )}
          </div>
          <p className="mt-1 text-[13px] text-muted">
            Новые запуски агентов не стартуют. Уже идущие доработают текущий шаг. Задачи и вопросы сохраняются.
          </p>
        </div>
        <Button
          variant={settings.paused ? 'primary' : 'secondary'}
          icon={settings.paused ? 'play' : 'pause'}
          loading={busy}
          onClick={() =>
            void run(async () => {
              const s = await patchSettings({ paused: !settings.paused })
              if (s) toast(s.paused ? 'Пауза включена' : 'Пауза снята', 'success')
            })
          }
        >
          {settings.paused ? 'Снять паузу' : 'Поставить всё на паузу'}
        </Button>
      </div>
    </Section>
  )
}

function ProvidersSection({ settings }: { settings: Settings }) {
  const [provider, setProvider] = useState(settings.preferred_provider)
  const [fallback, setFallback] = useState(settings.provider_fallback)
  const [profiles, setProfiles] = useState(settings.model_profiles)
  const [busy, run] = useBusy()
  const id = useId()
  const dirty = provider !== settings.preferred_provider || fallback !== settings.provider_fallback || JSON.stringify(profiles) !== JSON.stringify(settings.model_profiles)
  const valid = Object.values(profiles).every((p) => p.standard.trim() && p.strong.trim())
  const model = (engine: 'claude' | 'codex', level: 'standard' | 'strong', value: string) =>
    setProfiles((old) => ({ ...old, [engine]: { ...old[engine], [level]: value } }))
  const save = () => void run(async () => {
    const s = await patchSettings({ preferred_provider: provider, provider_fallback: fallback, model_profiles: profiles })
    if (s) toast('Выбор движка и модели сохранён. Новые ходы используют эти настройки.', 'success')
  })
  return <Section title="Движок и модели агентов" actions={dirty ? <Button size="sm" variant="primary" loading={busy} disabled={busy || !valid} onClick={save}>Сохранить</Button> : null}>
    <div className="grid gap-4 md:grid-cols-2">
      <Field label="Основной движок" htmlFor={`${id}-provider`} hint="Для новых задач и следующих ходов уже начатых задач. Текущие ходы продолжают работу.">
        <select id={`${id}-provider`} className="ctl" value={provider} onChange={(e) => setProvider(e.target.value as 'claude' | 'codex')}>
          <option value="codex">Codex</option>
          <option value="claude">Claude</option>
        </select>
      </Field>
      <Toggle checked={fallback} onChange={setFallback} label="Использовать другой движок как резерв"
        description="Если у основного нет доступных мест, работу берёт другой подходящий раннер. Ограничения доступа задачи сохраняются." />
    </div>
    {!fallback ? <p className="mt-3 text-[12.5px] text-muted">Без резерва задачи ждут основной движок, даже если у другого есть свободные места.</p> : null}
    <div className="mt-5 grid gap-4 md:grid-cols-2">
      {(['claude', 'codex'] as const).map((engine) => <div key={engine} className="min-w-0 rounded-ctl border border-line p-3">
        <h3 className="mb-3 text-[13.5px] font-semibold">{engine === 'claude' ? 'Claude' : 'Codex'}</h3>
        <div className="flex flex-col gap-3">
          {(['standard', 'strong'] as const).map((level) => <Field key={level} label={level === 'standard' ? 'Основная модель' : 'Сильная модель'}
            htmlFor={`${id}-${engine}-${level}`} hint={level === 'strong' ? 'Штаб, лиды и другие агенты уровня Opus.' : 'Обычные исполнители и агенты уровня Sonnet.'}>
            {engine === 'claude' ? <ModelInput id={`${id}-${engine}-${level}`} value={profiles[engine][level]} onChange={(v) => model(engine, level, v)} />
              : <input id={`${id}-${engine}-${level}`} className="ctl font-mono" value={profiles[engine][level]}
                onChange={(e) => model(engine, level, e.target.value.trim())} spellCheck={false} required />}
          </Field>)}
        </div>
      </div>)}
    </div>
    <p className="mt-3 text-[12.5px] text-muted">При переходе между Claude и Codex уровень агента и контекст задачи сохраняются. Фактический движок и модель видны в карточке агента.</p>
  </Section>
}

function ParamsSection({ settings }: { settings: Settings }) {
  const [maxConc, setMaxConc] = useState(settings.max_concurrency)
  const [budget, setBudget] = useState(settings.daily_budget_usd)
  const [hosts, setHosts] = useState<string[]>(settings.blocked_hosts)
  const [roHosts, setRoHosts] = useState<string[]>(settings.readonly_hosts)
  const [busy, run] = useBusy()
  const f = { conc: useId(), budget: useId(), model: useId(), hosts: useId(), ro: useId() }

  const dirty =
    maxConc !== settings.max_concurrency ||
    budget !== settings.daily_budget_usd ||
    hosts.join('\n') !== settings.blocked_hosts.join('\n') ||
    roHosts.join('\n') !== settings.readonly_hosts.join('\n')

  const save = () =>
    run(async () => {
      const s = await patchSettings({
        max_concurrency: maxConc,
        daily_budget_usd: budget,
        blocked_hosts: hosts,
        readonly_hosts: roHosts,
      })
      if (s) toast('Параметры сохранены', 'success')
    })

  return (
    <Section
      title="Параметры"
      actions={
        dirty ? (
          <Button size="sm" variant="primary" loading={busy} onClick={() => void save()}>
            Сохранить
          </Button>
        ) : null
      }
    >
      <div className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2">
        <Field label="Одновременных запусков (max_concurrency)" htmlFor={f.conc} hint="Сколько агентов может работать параллельно на всех раннерах.">
          <input
            id={f.conc}
            type="number"
            min={0}
            max={200}
            className="ctl w-32"
            value={maxConc}
            onChange={(e) => setMaxConc(Math.max(0, Number(e.target.value) || 0))}
          />
        </Field>
        <Field label="Дневной бюджет, $ (daily_budget_usd)" htmlFor={f.budget} hint="При превышении новые запуски не стартуют. 0 — без лимита.">
          <input
            id={f.budget}
            type="number"
            min={0}
            step={1}
            className="ctl w-32"
            value={budget}
            onChange={(e) => setBudget(Math.max(0, Number(e.target.value) || 0))}
          />
        </Field>
        <Field
          label="Время агентов"
          htmlFor={`${f.model}-tz`}
          hint="По нему OMA называет агентам время, по нему же сроки ожиданий на карточках и часы в шапке. Применяется сразу."
        >
          <select
            id={`${f.model}-tz`}
            className="ctl w-64 max-w-full"
            value={settings.agent_timezone}
            onChange={(e) => void patchSettings({ agent_timezone: e.target.value })}
          >
            {[...AGENT_ZONES, ...(AGENT_ZONES.some((z) => z.id === settings.agent_timezone) ? [] : [{ id: settings.agent_timezone, label: settings.agent_timezone }])].map((z) => (
              <option key={z.id} value={z.id}>
                {z.label} · сейчас {agentTime(Date.now(), z.id, { label: false })}
              </option>
            ))}
          </select>
        </Field>
        <div className="flex flex-col gap-3">
          <Toggle
            checked={settings.review_required}
            onChange={(v) => void patchSettings({ review_required: v })}
            label="Обязательная проверка"
            description="Перед «Готово» задачу проверяют тестировщик и валидатор."
          />
          <Toggle
            checked={settings.auto_triage}
            onChange={(v) => void patchSettings({ auto_triage: v })}
            label="Авто-разбор входящих"
            description="Штаб сам берёт новые задачи в разбор и раскладывает по направлениям."
          />
          <Toggle
            checked={settings.docs_refresh === 'daily'}
            onChange={(v) => void patchSettings({ docs_refresh: v ? 'daily' : 'off' })}
            label="Ежедневная актуализация справочников по коммитам"
            description="Раз в сутки OMA сверяет справочники с новыми коммитами и ставит агенту сервиса задачу обновить устаревшие разделы (только чтение)."
          />
        </div>
        <div className="md:col-span-2">
          <Field
            label="Запрещённые хосты (blocked_hosts)"
            htmlFor={f.hosts}
            hint="Агенты не подключаются к этим серверам ни в каком режиме. Enter — добавить."
          >
            <HostsInput id={f.hosts} hosts={hosts} onChange={setHosts} placeholder="например, 77.222.42.31 или dashboards" />
          </Field>
        </div>
        <div className="md:col-span-2">
          <Field
            label="Хосты только для чтения (readonly_hosts)"
            htmlFor={f.ro}
            hint="Серверы чужих команд (например, 2С): даже в полном режиме агент там только читает — без правок файлов, рестартов и записи в базу."
          >
            <HostsInput id={f.ro} hosts={roHosts} onChange={setRoHosts} placeholder="например, 46.225.101.118 или oneofs" />
          </Field>
        </div>
      </div>
    </Section>
  )
}

function HostsInput({ id, hosts, onChange, placeholder }: { id: string; hosts: string[]; onChange: (h: string[]) => void; placeholder: string }) {
  const [input, setInput] = useState('')
  const add = () => {
    const parts = input
      .split(/[\s,]+/)
      .map((h) => h.trim())
      .filter(Boolean)
    if (!parts.length) return
    onChange([...hosts, ...parts.filter((p) => !hosts.includes(p))])
    setInput('')
  }
  return (
    <div className="flex flex-wrap items-center gap-1.5 rounded-ctl border border-line-strong bg-surface p-1.5">
      {hosts.map((h) => (
        <span key={h} className="inline-flex h-6 max-w-full items-center gap-1 rounded-[5px] border border-line bg-surface-2 pr-0.5 pl-2 font-mono text-[12px] text-fg-2">
          <span className="min-w-0 truncate" title={h}>
            {h}
          </span>
          <button
            type="button"
            className="shrink-0 rounded p-0.5 text-muted hover:bg-surface-3 hover:text-fg"
            aria-label={`Убрать ${h}`}
            onClick={() => onChange(hosts.filter((x) => x !== h))}
          >
            <Icon name="x" size={11} />
          </button>
        </span>
      ))}
      <input
        id={id}
        className="min-w-[140px] flex-1 bg-transparent px-1 py-0.5 font-mono text-[12px] outline-none placeholder:text-subtle"
        placeholder={hosts.length ? 'ещё хост…' : placeholder}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault()
            add()
          } else if (e.key === 'Backspace' && !input && hosts.length) {
            onChange(hosts.slice(0, -1))
          }
        }}
        onBlur={add}
      />
    </div>
  )
}

function RunnersSection() {
  const runners = useLive((s) => s.runners)
  const now = useNow()
  const sorted = useMemo(() => [...runners].sort((a, b) => Number(b.status === 'online') - Number(a.status === 'online') || a.name.localeCompare(b.name)), [runners])
  return (
    <Section title="Раннеры" bodyClassName="p-0">
      {sorted.length === 0 ? (
        <div className="px-[18px] py-4">
          <Empty icon="server">
            Ни одного раннера онлайн — агенты не запускаются. Создай токен раннера ниже, положи его в <code className="font-mono [overflow-wrap:anywhere]">~/.oma/runner.token</code> и выполни в репозитории OMA <code className="font-mono [overflow-wrap:anywhere]">bash ops/install-runner-mac.sh</code> (Mac) — или поставь <code className="font-mono [overflow-wrap:anywhere]">ops/systemd/oma-runner.service</code> на сервер.
          </Empty>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="dt min-w-[640px]">
            <thead>
              <tr>
                <th scope="col" className="!pl-[18px]">Раннер</th>
                <th scope="col">Состояние</th>
                <th scope="col">Метки</th>
                <th scope="col" className="r">Занято / мест</th>
                <th scope="col">Версия</th>
                <th scope="col" className="r !pr-[18px]">Был на связи</th>
                <th scope="col">Управление</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => (
                <tr key={r.id}>
                  <td className="!pl-[18px]">
                    <div className="font-medium">{r.name}</div>
                    <div className="font-mono text-[11px] text-subtle">{r.id}</div>
                  </td>
                  <td>
                    {r.status === 'online' ? (
                      <Badge tone="ok" dot>
                        онлайн
                      </Badge>
                    ) : (
                      <Badge>офлайн</Badge>
                    )}
                    {(r.effective_state ?? 'active') !== 'active' ? <div className="mt-1"><Badge tone="warn">{r.effective_state === 'draining' && r.running ? 'завершает ходы' : 'на паузе'}</Badge></div> : null}
                  </td>
                  <td>
                    <div className="flex flex-wrap gap-1">
                      {r.labels.map((l) => (
                        <Badge key={l}>{l}</Badge>
                      ))}
                    </div>
                  </td>
                  <td className="tnum r">
                    {r.running} / {r.capacity}
                  </td>
                  <td className="font-mono text-[12px] text-muted">{r.version}</td>
                  <td className="tnum r !pr-[18px] text-muted" title={formatDateTime(r.last_seen)}>
                    {timeAgo(r.last_seen, now)}
                  </td>
                  <td>
                    <FleetControls path={`/api/runners/${encodeURIComponent(r.id)}`} state={r.desired_state ?? 'active'} name={r.name} onChanged={() => void refreshSettings()} />
                    {(r.desired_state ?? 'active') === 'active' && (r.effective_state ?? 'active') !== 'active' ? <p className="mt-1 text-[12px] text-muted">Пауза всей машины — управление на странице «Машины».</p> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  )
}

function fillToken(cmd: string, token: string): string {
  const replaced = cmd.replace(/<[^>]*(токен|token)[^>]*>/i, token)
  if (replaced !== cmd) return replaced
  return cmd.replace(/(Bearer\s+)(\S+?)(["']?)$/, `$1${token}$3`)
}

function CopyBox({ value, label, mono = true }: { value: string; label: string; mono?: boolean }) {
  const [copied, setCopied] = useState(false)
  return (
    <div className="flex items-stretch gap-1.5">
      <code
        className={cx(
          // на телефоне команда переносится строками — видна целиком, без прокрутки вбок
          'scroll-thin min-w-0 flex-1 overflow-x-auto rounded-ctl border border-line bg-surface-2 px-2.5 py-1.5 text-[12px] whitespace-nowrap max-sm:whitespace-pre-wrap max-sm:[overflow-wrap:anywhere]',
          mono && 'font-mono',
        )}
        aria-label={label}
      >
        {value}
      </code>
      <Button
        size="sm"
        icon={copied ? 'check' : 'copy'}
        className="h-auto shrink-0"
        onClick={async () => {
          const ok = await copyText(value)
          if (ok) {
            setCopied(true)
            window.setTimeout(() => setCopied(false), 1600)
          } else toast('Не удалось скопировать — выдели и скопируй вручную', 'error')
        }}
        aria-label={`Скопировать: ${label}`}
      >
        {/* на телефоне — только значок, место отдано команде */}
        <span className="max-sm:sr-only">{copied ? 'Скопировано' : 'Копировать'}</span>
      </Button>
    </div>
  )
}

interface SecretInfo { name: string; purpose: string; updated_at: string; updated_by: string; used_at: string | null }

/** Секреты для агентов: значения вводятся сюда (или в ответ на запрос агента) и никогда не показываются. */
const CHAT_MODES: { id: WorkChatMode; label: string; hint: string }[] = [
  { id: 'off', label: 'Выключен', hint: 'робот чат не читает' },
  { id: 'observe', label: 'Наблюдение', hint: 'читает и разбирает, в чат ничего не пишет и задач не ставит — вердикты видны в задаче разбора' },
  { id: 'work', label: 'Работа', hint: 'сам берёт задачи, отвечает в чат и публикует итоги' },
]

// приветствие приходит с сервера — своё у каждого чата; это — на случай старого сервера
const FALLBACK_INTRO =
  'Всем привет! Я OMA — робот-помощник. Читаю этот чат и беру в работу сообщения о сбоях и проблемах. ' +
  'Когда беру — отвечаю «Взял в работу: OMA-…», итог пишу ответом на исходное сообщение. Решения, деньги и доступы остаются за вами. ' +
  'Позвать меня напрямую — упомяните @OMA или ответьте на моё сообщение.'

interface ChatLogRow { message_id: number; author: string; date: string; text: string; from_bot: number; state: string; verdict: string | null; task_id: number | null }

const VERDICTS: Record<string, string> = {
  task: 'задача', followup: 'продолжение', answer: 'ответ', skip: 'не наше', over_budget: 'бюджет исчерпан', unprocessed: 'не разобрано',
}

function WorkChatRow({ chat, onSaved }: { chat: WorkChat; onSaved: (c: WorkChat) => void }) {
  const [assignee, setAssignee] = useState(chat.assignee_agent_id ?? '')
  const [budget, setBudget] = useState(String(chat.daily_budget_usd))
  const [notes, setNotes] = useState(chat.notes)
  // робот мог дописать черновик правил, пока страница открыта
  useEffect(() => setNotes(chat.notes), [chat.notes])
  const [intro, setIntro] = useState<string | null>(null)
  const [log, setLog] = useState<ChatLogRow[] | null>(null)
  const [busy, run] = useBusy()
  const ids = { assignee: useId(), budget: useId(), notes: useId(), intro: useId() }
  const save = (patch: Partial<WorkChat> & { intro?: string }) =>
    run(async () => {
      try {
        const c = await api.put<WorkChat>(`/api/work-chats/${chat.dialog_id}`, patch)
        if (c) onSaved(c)
        setIntro(null)
        toast('Сохранено', 'success')
      } catch (e) {
        toastError(e, 'Не сохранено')
      }
    })
  const st = chat.stats
  return (
    <li className="flex flex-col gap-3 px-3.5 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <Icon name="message" size={15} className="shrink-0 text-subtle" />
        <span className="min-w-0 text-[14px] font-semibold [overflow-wrap:anywhere]">{chat.title || chat.dialog_id}</span>
        <span className="font-mono text-[12px] text-subtle">{chat.dialog_id}</span>
        <Badge tone={chat.mode === 'work' ? 'ok' : chat.mode === 'observe' ? 'accent' : 'neutral'}>{CHAT_MODES.find((m) => m.id === chat.mode)?.label}</Badge>
        <span className="ml-auto" />
        <div className="inline-flex max-w-full shrink-0 overflow-hidden rounded-ctl border border-line max-sm:w-full" role="radiogroup" aria-label="Режим робота">
          {CHAT_MODES.map((m) => (
            <button
              key={m.id}
              type="button"
              role="radio"
              aria-checked={chat.mode === m.id}
              title={m.hint}
              disabled={busy}
              className={cx('px-2.5 py-1 text-[12.5px] whitespace-nowrap max-sm:flex-1 max-sm:px-1.5', chat.mode === m.id ? 'bg-accent text-accent-fg' : 'bg-surface hover:bg-surface-2')}
              onClick={() => {
                if (m.id === chat.mode) return
                if (m.id === 'work') setIntro(chat.intro || FALLBACK_INTRO)
                else void save({ mode: m.id })
              }}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>
      {st ? (
        <div className="text-[12.5px] break-words text-muted">
          Сегодня: сообщений {st.messages_today}, задач {st.tasks_today} · открыто задач {st.open_tasks} · ждут разбора {st.pending} · потрачено {formatUsd(st.cost_today_usd)} из {formatUsd(chat.daily_budget_usd)}
          {chat.triage_task_id ? (
            <>
              {' · '}
              <TaskLink id={chat.triage_task_id} className="hover:underline">
                разбор за сегодня
              </TaskLink>
            </>
          ) : null}
        </div>
      ) : null}
      {intro !== null ? (
        <div className="rounded-card border border-line bg-surface-2 p-3">
          <Field label="Приветствие — робот напишет его в чат, когда включится «Работа»" htmlFor={ids.intro}>
            <textarea id={ids.intro} className="ctl min-h-[96px]" value={intro} onChange={(e) => setIntro(e.target.value)} />
          </Field>
          <p className="mt-1.5 text-[12px] text-subtle">Бот OMA должен быть участником чата — добавьте его в чат в Битрикс24 до включения.</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <Button size="sm" variant="primary" loading={busy} onClick={() => void save({ mode: 'work', intro: intro.trim() })}>
              Включить и отправить
            </Button>
            <Button size="sm" variant="ghost" onClick={() => void save({ mode: 'work' })}>
              Включить без приветствия
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setIntro(null)}>
              Отмена
            </Button>
          </div>
        </div>
      ) : null}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_160px]">
        <Field label="Кому идут задачи из чата" htmlFor={ids.assignee}>
          <AgentSelect id={ids.assignee} value={assignee} onChange={setAssignee} emptyLabel="— не задан —" />
        </Field>
        <Field label="Бюджет в день, $" htmlFor={ids.budget}>
          <input id={ids.budget} className="ctl tnum" inputMode="decimal" value={budget} onChange={(e) => setBudget(e.target.value)} />
        </Field>
        <div className="md:col-span-2">
          <Field
            label={
              <>
                Правила чата — для робота и исполнителей: кто есть кто, что брать, чего не трогать
                {chat.notes_draft ? (
                  <Badge tone="warn" className="ml-2 align-middle normal-case">
                    черновик робота
                  </Badge>
                ) : null}
              </>
            }
            hint={
              chat.notes_draft
                ? 'Робот составил правила по истории чата. Проверьте, поправьте и нажмите «Сохранить» — после этого они станут вашими.'
                : !chat.notes.trim()
                  ? 'Правил пока нет — робот составит черновик при следующем анализе направления.'
                  : undefined
            }
            htmlFor={ids.notes}
          >
            <textarea id={ids.notes} className="ctl min-h-[120px] font-[inherit] text-[13px]" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="primary"
          loading={busy}
          onClick={() => {
            const b = Number(budget.replace(',', '.'))
            if (!Number.isFinite(b) || b < 0) return void toast('Бюджет — число долларов в день', 'error')
            void save({ assignee_agent_id: assignee || null, daily_budget_usd: b, notes })
          }}
        >
          Сохранить
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() =>
            void api
              .get<ChatLogRow[]>(`/api/work-chats/${chat.dialog_id}/messages?limit=40`)
              .then((r) => setLog(r ?? []))
              .catch((e) => toastError(e, 'Не загрузилось'))
          }
        >
          Последние сообщения и решения
        </Button>
      </div>
      {log ? (
        <ul className="flex max-h-[360px] flex-col gap-1.5 overflow-auto rounded-card border border-line p-2.5 text-[12.5px] max-sm:gap-2.5">
          {log.length ? (
            log.map((m) => (
              // на телефоне: дата, автор и решение строкой, текст сообщения — под ними на всю ширину
              <li key={m.message_id} className="flex gap-2 max-sm:flex-wrap max-sm:items-center max-sm:gap-x-2 max-sm:gap-y-0.5">
                <span className="tnum shrink-0 text-subtle">{formatDateTime(m.date || '')}</span>
                <span className={cx('shrink-0 font-medium', m.from_bot ? 'text-accent' : 'text-fg')}>{m.from_bot ? 'OMA' : m.author}</span>
                <span className="min-w-0 flex-1 truncate text-fg-2 max-sm:order-last max-sm:line-clamp-3 max-sm:basis-full max-sm:whitespace-normal max-sm:[overflow-wrap:anywhere]" title={m.text}>
                  {m.text}
                </span>
                {m.verdict ? <Badge tone={m.verdict === 'task' ? 'ok' : m.verdict === 'skip' ? 'neutral' : 'accent'}>{VERDICTS[m.verdict] ?? m.verdict}</Badge> : m.state === 'new' || m.state === 'triaging' ? <Badge tone="warn">ждёт разбора</Badge> : null}
                {m.task_id ? (
                  <TaskLink id={m.task_id} className="shrink-0 font-mono text-[11.5px] hover:underline">
                    OMA-{m.task_id}
                  </TaskLink>
                ) : null}
              </li>
            ))
          ) : (
            <li className="text-subtle">Сообщений пока нет.</li>
          )}
        </ul>
      ) : null}
    </li>
  )
}

function WorkChatsSection() {
  const list = useAsync((signal) => api.get<WorkChat[]>('/api/work-chats', { signal }), [])
  const [dialog, setDialog] = useState('')
  const [title, setTitle] = useState('')
  const [direction, setDirection] = useState('')
  const directions = useLive((s) => s.directions)
  const [busy, run] = useBusy()
  const ids = { dialog: useId(), title: useId(), direction: useId() }
  const add = () =>
    run(async () => {
      try {
        await api.put(`/api/work-chats/${dialog.trim()}`, { title: title.trim(), direction_id: direction || null, mode: 'observe' })
        setDialog('')
        setTitle('')
        list.reload()
        toast('Чат подключён в режиме наблюдения', 'success')
      } catch (e) {
        toastError(e, 'Не подключён')
      }
    })
  return (
    <Section title="Рабочие чаты (робот OMA в Битрикс24)">
      <p className="mb-3 text-[13px] text-muted">
        Робот читает рабочий чат направления целиком (вашим доступом — вы должны быть участником чата), разбирает новые сообщения и сам берёт задачи: ставит их агентам, отвечает в чат и
        публикует итог ответом на исходное сообщение. Начните с «Наблюдения» — посмотрите разбор в задаче дня, затем включите «Работу».
      </p>
      {list.error && !list.data ? (
        <ErrorBox error={list.error} onRetry={list.reload} />
      ) : !list.data ? (
        <Loading rows={1} />
      ) : list.data.length ? (
        <ul className="mb-4 flex flex-col divide-y divide-line rounded-card border border-line">
          {list.data.map((c) => (
            <WorkChatRow key={`${c.dialog_id}:${c.updated_at}`} chat={c} onSaved={() => list.reload()} />
          ))}
        </ul>
      ) : (
        <p className="mb-4 text-[13px] text-subtle">Рабочих чатов пока нет.</p>
      )}
      <form
        className="grid grid-cols-1 gap-3 md:grid-cols-[180px_1fr_200px_auto] md:items-end"
        onSubmit={(e) => {
          e.preventDefault()
          if (!/^chat\d+$/.test(dialog.trim())) return void toast('Чат — вида chat76070 (из адреса чата в Битрикс24)', 'error')
          void add()
        }}
      >
        <Field label="Чат" htmlFor={ids.dialog}>
          <input id={ids.dialog} className="ctl font-mono" value={dialog} onChange={(e) => setDialog(e.target.value.trim())} placeholder="chat76070" />
        </Field>
        <Field label="Название" htmlFor={ids.title}>
          <input id={ids.title} className="ctl" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="buying v. 2" />
        </Field>
        <Field label="Направление" htmlFor={ids.direction}>
          <select id={ids.direction} className="ctl" value={direction} onChange={(e) => setDirection(e.target.value)}>
            <option value="">—</option>
            {directions.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </Field>
        <Button type="submit" size="sm" variant="primary" loading={busy}>
          Подключить
        </Button>
      </form>
    </Section>
  )
}

function SecretsSection() {
  const list = useAsync((signal) => api.get<SecretInfo[]>('/api/secrets', { signal }), [])
  const now = useNow()
  const [name, setName] = useState('')
  const [value, setValue] = useState('')
  const [purpose, setPurpose] = useState('')
  const [replacing, setReplacing] = useState<string | null>(null)
  const [busy, run] = useBusy()
  const ids = { name: useId(), value: useId(), purpose: useId() }
  const save = (n: string, v: string, p = '') =>
    run(async () => {
      try {
        await api.put(`/api/secrets/${encodeURIComponent(n)}`, { value: v, purpose: p })
        toast(`${n} сохранён`, 'success')
        setName('')
        setValue('')
        setPurpose('')
        setReplacing(null)
        list.reload()
      } catch (e) {
        toastError(e, 'Не сохранено')
      }
    })
  const remove = (n: string) =>
    run(async () => {
      if (!window.confirm(`Удалить ${n}? Агенты больше не смогут его подставлять.`)) return
      try {
        await api.del(`/api/secrets/${encodeURIComponent(n)}`)
        list.reload()
      } catch (e) {
        toastError(e, 'Не удалено')
      }
    })
  return (
    <Section title="Секреты для агентов">
      <p className="mb-3 text-[13px] text-muted">
        Токены и пароли, которые агенты сами записывают в настройки сервисов. Агент значения не видит: в команде он пишет {'{{secret:ИМЯ}}'},
        а OMA подставляет значение только при записи в .env. Обычно агент сам попросит нужный секрет в «Ждёт тебя»; сюда можно положить заранее или заменить.
      </p>
      {list.error && !list.data ? (
        <ErrorBox error={list.error} onRetry={list.reload} />
      ) : !list.data ? (
        <Loading rows={1} />
      ) : list.data.length ? (
        <ul className="mb-4 flex flex-col divide-y divide-line rounded-card border border-line">
          {list.data.map((s) => (
            <li key={s.name} className="flex flex-col gap-1.5 px-3 py-2.5">
              <div className="flex flex-wrap items-center gap-2">
                <Icon name="key" size={14} className="shrink-0 text-subtle" />
                <span className="min-w-0 font-mono text-[13px] font-semibold [overflow-wrap:anywhere]">{s.name}</span>
                <span className="text-[12px] text-subtle">
                  задан {timeAgo(s.updated_at, now)}
                  {s.used_at ? ` · использован ${timeAgo(s.used_at, now)}` : ' · ещё не использован'}
                </span>
                <span className="ml-auto" />
                <Button size="xs" variant="ghost" onClick={() => setReplacing(replacing === s.name ? null : s.name)}>
                  Заменить
                </Button>
                <Button size="xs" variant="ghost" onClick={() => void remove(s.name)}>
                  Удалить
                </Button>
              </div>
              {s.purpose ? <div className="line-clamp-2 text-[12px] [overflow-wrap:anywhere] text-muted">{s.purpose}</div> : null}
              {replacing === s.name ? (
                <form
                  className="flex gap-2"
                  autoComplete="off"
                  onSubmit={(e) => {
                    e.preventDefault()
                    void save(s.name, value)
                  }}
                >
                  <input type="password" className="ctl flex-1 font-mono" autoComplete="new-password" value={value} onChange={(e) => setValue(e.target.value)} placeholder="новое значение" />
                  <Button type="submit" size="sm" variant="primary" loading={busy}>
                    Сохранить
                  </Button>
                </form>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mb-4 text-[13px] text-subtle">Секретов пока нет.</p>
      )}
      <form
        className="grid grid-cols-1 gap-3 md:grid-cols-2"
        autoComplete="off"
        onSubmit={(e) => {
          e.preventDefault()
          if (!/^[A-Z][A-Z0-9_]{1,63}$/.test(name.trim())) return void toast('Имя — как в .env: ЗАГЛАВНЫЕ_БУКВЫ_И_ЦИФРЫ', 'error')
          if (!value.trim()) return void toast('Вставьте значение', 'error')
          void save(name.trim(), value.trim(), purpose.trim())
        }}
      >
        <Field label="Имя, как в .env" htmlFor={ids.name}>
          <input id={ids.name} className="ctl font-mono" value={name} onChange={(e) => setName(e.target.value.toUpperCase())} placeholder="CLOUDFLARE_ZONES_TOKEN" />
        </Field>
        <Field label="Значение" htmlFor={ids.value}>
          <input id={ids.value} type="password" className="ctl font-mono" autoComplete="new-password" value={value} onChange={(e) => setValue(e.target.value)} />
        </Field>
        <div className="md:col-span-2">
          <Field label="Для чего (необязательно)" htmlFor={ids.purpose}>
            <input id={ids.purpose} className="ctl" value={purpose} onChange={(e) => setPurpose(e.target.value)} placeholder="Медузатор: создание зон Cloudflare для новых доменов" />
          </Field>
        </div>
        <div className="md:col-span-2">
          <Button type="submit" variant="primary" size="sm" loading={busy}>
            Добавить секрет
          </Button>
        </div>
      </form>
    </Section>
  )
}

function TokensSection() {
  const now = useNow()
  const tokens = useAsync((signal) => api.get<ApiToken[]>('/api/tokens', { signal }), [])
  const connect = useAsync((signal) => api.get<ConnectInfo>('/api/connect', { signal }), [])
  const [name, setName] = useState('')
  const [kind, setKind] = useState<'personal' | 'runner'>('personal')
  const [subject, setSubject] = useState('')
  const [formError, setFormError] = useState<string | null>(null)
  const [created, setCreated] = useState<{ token: string; kind: 'personal' | 'runner'; name: string } | null>(null)
  const [busy, run] = useBusy()
  const f = { name: useId(), kind: useId(), subject: useId() }

  const create = () =>
    run(async () => {
      setFormError(null)
      if (!name.trim()) {
        setFormError('Дай токену имя — например, «ноутбук Сергея»')
        return
      }
      const sub = subject.trim()
      if (kind === 'personal' && sub && !/^[a-z0-9-]{2,40}$/.test(sub)) {
        setFormError('id внешнего агента: 2–40 символов — латиница в нижнем регистре, цифры, дефис')
        return
      }
      try {
        const body: Record<string, unknown> = { name: name.trim(), kind }
        if (kind === 'personal' && sub) body.subject = sub
        const r = await api.post<{ id: number | string; token: string }>('/api/tokens', body)
        setCreated({ token: r.token, kind, name: name.trim() })
        setName('')
        setSubject('')
        tokens.reload()
      } catch (e) {
        setFormError(errorMessage(e))
        toastError(e, 'Токен не создан')
      }
    })

  const revoke = async (t: ApiToken) => {
    if (!window.confirm(`Отозвать токен «${t.name}»? Всё, что им пользуется, сразу потеряет доступ.`)) return
    try {
      await api.del(`/api/tokens/${t.id}`)
      toast('Токен отозван', 'success')
      tokens.reload()
    } catch (e) {
      toastError(e, 'Не удалось отозвать')
    }
  }

  return (
    <Section title="Токены и подключение Claude Code">
      <div className="flex flex-col gap-4">
        {connect.data ? (
          <div className="rounded-card border border-line bg-surface-2 p-3.5">
            <div className="text-[13.5px] font-semibold">Подключить свой Claude Code к OMA</div>
            <p className="mt-0.5 mb-2.5 text-[13px] text-muted">
              MCP-сервер: <code className="font-mono [overflow-wrap:anywhere]">{connect.data.mcp_url}</code>. Создай личный токен — команда ниже подставит его сама.
            </p>
            <CopyBox value={connect.data.claude_mcp_add} label="Команда подключения" />
          </div>
        ) : connect.error ? (
          <ErrorBox error={connect.error} onRetry={connect.reload} />
        ) : null}

        {created ? (
          <div className="rounded-card border border-line bg-surface p-3.5 shadow-[inset_3px_0_0_var(--orbis-good)]" role="status">
            <div className="flex items-center gap-1.5 text-[13.5px] font-semibold break-words text-ok">
              <Icon name="check" size={14} className="shrink-0" />
              Токен «{created.name}» создан — сохрани его сейчас, больше он не покажется
            </div>
            <div className="mt-2 flex flex-col gap-2">
              <CopyBox value={created.token} label="Значение токена" />
              {created.kind === 'personal' && connect.data ? (
                <>
                  <div className="text-[12px] text-muted">Готовая команда:</div>
                  <CopyBox value={fillToken(connect.data.claude_mcp_add, created.token)} label="Команда claude mcp add с токеном" />
                </>
              ) : null}
              {created.kind === 'runner' ? (
                <div className="text-[12px] text-muted">Сохрани токен в <code className="font-mono">~/.oma/runner.token</code> (права 600) на машине раннера и запусти <code className="font-mono">bash ops/install-runner-mac.sh</code> — раннер встанет службой и подключится к OMA.</div>
              ) : null}
            </div>
            <div className="mt-2 flex justify-end">
              <Button size="sm" variant="secondary" onClick={() => setCreated(null)}>
                Я сохранил, скрыть
              </Button>
            </div>
          </div>
        ) : null}

        <form
          className="flex flex-col gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            void create()
          }}
          aria-describedby={formError ? `${f.name}-err` : undefined}
        >
          <div className={cx('grid grid-cols-1 items-end gap-2.5', kind === 'personal' ? 'sm:grid-cols-[1fr_170px_1.2fr_auto]' : 'sm:grid-cols-[1fr_170px_auto]')}>
            <Field label="Имя токена" htmlFor={f.name}>
              <input id={f.name} className="ctl" value={name} onChange={(e) => setName(e.target.value)} placeholder="ноутбук, раннер на хабе…" />
            </Field>
            <Field label="Тип" htmlFor={f.kind}>
              <select
                id={f.kind}
                className="ctl"
                value={kind}
                onChange={(e) => {
                  setKind(e.target.value as 'personal' | 'runner')
                  setFormError(null)
                }}
              >
                <option value="personal">личный (MCP)</option>
                <option value="runner">раннер</option>
              </select>
            </Field>
            {kind === 'personal' ? (
              <Field label="Внешний агент" htmlFor={f.subject}>
                <input
                  id={f.subject}
                  className="ctl font-mono placeholder:font-sans"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value.toLowerCase())}
                  placeholder="id внешнего агента, необязательно (латиница, дефис)"
                  autoComplete="off"
                  spellCheck={false}
                />
              </Field>
            ) : null}
            <Button type="submit" variant="primary" icon="plus" loading={busy}>
              Создать токен
            </Button>
          </div>
          <p className="text-[12.5px] text-muted">
            {kind === 'personal'
              ? 'Личный токен привязывается к внешнему агенту: укажи его id или оставь пустым — будет создан ext-<имя>.'
              : 'Токен раннера получает субъект автоматически.'}
          </p>
          {formError ? (
            <div id={`${f.name}-err`} role="alert" className="rounded-ctl bg-danger-soft px-3 py-2 text-[13px] text-danger">
              {formError}
            </div>
          ) : null}
        </form>

        {tokens.error && !tokens.data ? (
          <ErrorBox error={tokens.error} onRetry={tokens.reload} />
        ) : !tokens.data ? (
          <Loading />
        ) : tokens.data.length === 0 ? (
          <p className="text-[12.5px] text-muted">Токенов пока нет.</p>
        ) : (
          <div className="overflow-x-auto rounded-card border border-line">
            <table className="dt min-w-[620px]">
              <thead>
                <tr>
                  <th scope="col">Имя</th>
                  <th scope="col">Тип</th>
                  <th scope="col">Субъект</th>
                  <th scope="col">Создан</th>
                  <th scope="col">Использован</th>
                  <th scope="col" className="r">
                    <span className="sr-only">Действия</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {tokens.data.map((t) => (
                  <tr key={t.id} className={cx(t.revoked && 'opacity-55')}>
                    <td className="font-medium">{t.name}</td>
                    <td>
                      <Badge>
                        {t.kind === 'runner' ? 'раннер' : t.kind === 'personal' ? 'личный' : t.kind}
                      </Badge>
                    </td>
                    <td className="font-mono text-[12px] text-muted">{t.subject ?? '—'}</td>
                    <td className="text-muted" title={formatDateTime(t.created_at)}>
                      {timeAgo(t.created_at, now)}
                    </td>
                    <td className="text-muted" title={t.last_used_at ? formatDateTime(t.last_used_at) : undefined}>
                      {t.last_used_at ? timeAgo(t.last_used_at, now) : 'никогда'}
                    </td>
                    <td className="r">
                      {t.revoked ? (
                        <span className="text-[12px] text-subtle">отозван</span>
                      ) : (
                        <Button size="xs" variant="danger-quiet" onClick={() => void revoke(t)}>
                          Отозвать
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Section>
  )
}

function PasswordSection() {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [repeat, setRepeat] = useState('')
  const [busy, run] = useBusy()
  const f = { cur: useId(), next: useId(), rep: useId() }
  const tooShort = next.length > 0 && next.length < 10
  const mismatch = repeat.length > 0 && repeat !== next

  const submit = () =>
    run(async () => {
      if (!current || next.length < 10) {
        toast('Новый пароль — не короче 10 символов', 'error')
        return
      }
      if (next !== repeat) {
        toast('Новый пароль и повтор не совпадают', 'error')
        return
      }
      try {
        await api.post('/api/auth/password', { current, next }, { ignore401: true })
        setCurrent('')
        setNext('')
        setRepeat('')
        toast('Пароль изменён', 'success')
      } catch (e) {
        toastError(e, 'Пароль не изменён')
      }
    })

  return (
    <Section title="Пароль">
      <form
        className="grid grid-cols-1 items-end gap-2.5 sm:grid-cols-[1fr_1fr_1fr_auto]"
        onSubmit={(e) => {
          e.preventDefault()
          void submit()
        }}
      >
        <Field label="Текущий пароль" htmlFor={f.cur}>
          <input id={f.cur} type="password" className="ctl" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />
        </Field>
        <Field label="Новый пароль" htmlFor={f.next}>
          <input
            id={f.next}
            type="password"
            className="ctl"
            autoComplete="new-password"
            minLength={10}
            value={next}
            onChange={(e) => setNext(e.target.value)}
            aria-invalid={tooShort || undefined}
          />
        </Field>
        <Field label="Повтор" htmlFor={f.rep}>
          <input
            id={f.rep}
            type="password"
            className="ctl"
            autoComplete="new-password"
            value={repeat}
            onChange={(e) => setRepeat(e.target.value)}
            aria-invalid={mismatch || undefined}
          />
        </Field>
        <Button type="submit" variant="primary" loading={busy} disabled={!current || next.length < 10 || next !== repeat}>
          Сменить пароль
        </Button>
      </form>
      <p className={cx('mt-2 text-[12.5px]', tooShort || mismatch ? 'text-warn' : 'text-muted')}>
        {tooShort ? 'Не короче 10 символов.' : mismatch ? 'Повтор не совпадает с новым паролем.' : 'Новый пароль — не короче 10 символов. После смены текущая сессия останется активной.'}
      </p>
    </Section>
  )
}

function StatsSection() {
  const stats = useLive((s) => s.stats)
  if (!stats) return null
  return (
    <Section title="Статистика">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <Stat label="Запусков сейчас" value={stats.jobs_running} tone={stats.jobs_running ? 'ok' : undefined} />
        <Stat label="В очереди" value={stats.jobs_queued} />
        <Stat label="Запусков сегодня" value={stats.jobs_today} />
        <Stat label="Расход сегодня" value={formatUsd(stats.cost_today_usd)} />
        <Stat label="Расход за 7 дней" value={formatUsd(stats.cost_7d_usd)} />
        <Stat label="Агентов работает" value={`${stats.agents_busy} / ${stats.agents_total}`} />
        <Stat label="Ждут тебя" value={stats.open_escalations} tone={stats.open_escalations ? 'warn' : undefined} />
        <Stat label="Активных заявок" value={stats.active_intents} />
        <Stat label="Раннеров онлайн" value={stats.runners_online} tone={stats.runners_online ? 'ok' : 'danger'} />
      </div>
      <div className="mt-4">
        <div className="lbl mb-2">Задачи по статусам</div>
        <div className="flex flex-wrap gap-1.5">
          {TASK_STATUSES.map((s) => (
            <span
              key={s}
              className={cx(
                'stbadge',
                (stats.tasks_by_status?.[s] ?? 0) > 0 && STATUS_TONES[s] === 'warn' && 'warn',
                (stats.tasks_by_status?.[s] ?? 0) > 0 && STATUS_TONES[s] === 'ok' && 'ok',
              )}
            >
              {TASK_STATUS_LABELS[s]}
              <b className="tnum font-semibold">{stats.tasks_by_status?.[s] ?? 0}</b>
            </span>
          ))}
        </div>
      </div>
    </Section>
  )
}
